import { Ref, ref } from '@vue/composition-api';
import { DialogInstance, DialogPlugin } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import { formatSize } from '@/renderer/scripts/utils/tools/index';
import tips from '@/renderer/scripts/utils/tips';
import { formatError } from '@/renderer/scripts/utils/errorCode/index';
import { TableData } from '@/renderer/scripts/composition/listOperateHandler';

type Handler = {
  dataTransferErrorTips: Ref<string>;
  personalCapacity: Ref<string>;
  createdGroupCapacity: Ref<string>;
  seletedUsers: Ref<SMH.UserBaseInfo[]>;
  noPersonalSpace: Ref<boolean>;

  deleteItem: (
    row: SMH.UserBaseInfo,
    { transferData, directDelte }: { transferData: (a: string, b: boolean) => void; directDelte: () => void },
  ) => void;
  deleteUsersFn: (
    tableData: TableData,
    { transferData, directDelte }: { transferData: (a: string, b: boolean) => void; directDelte: () => void },
  ) => void;
  deleteUser: (userId: number) => Promise<boolean>;
  deleteUsers: (list: any[], params?: SMH.DeleteUsersTransferParams) => Promise<boolean>;
  setUserEnabled: (list: any[], enabled: boolean) => Promise<boolean>;
  setUserRole: (list: any[], role: string) => Promise<boolean>;
  getUserOwnerGroup: (userIds: string) => Promise<SMH.GetUserOwnerGroupData[]>;
  activeUser: (userId: number) => Promise<boolean>;
  getUserCapacity: (list: any[]) => Promise<void>;
};
export default function actionHandler(): Handler {
  const dataTransferErrorTips = ref('');
  const personalCapacity = ref('');
  const createdGroupCapacity = ref('');
  const seletedUsers: Ref<SMH.UserBaseInfo[]> = ref([]);
  const noPersonalSpace: Ref<boolean> = ref(false);

  const methods = {
    // 删除单个用户
    async deleteItem(
      row: SMH.UserBaseInfo,
      { transferData, directDelte }: { transferData: (a: string, b: boolean) => void; directDelte: () => void },
    ) {
      const dialog: DialogInstance = DialogPlugin.confirm({
        theme: 'warning',
        header: i18n_t('是否需要进行删除成员的数据转接？'),
        body: h => {
          const baseTip = h('p', i18n_t('可以将用户的数据和权限移交给企业其他成员'));
          return h('div', [baseTip]);
        },
        confirmBtn: i18n_t('数据转接'),
        cancelBtn: i18n_t('直接删除'),
        // 数据转接
        async onConfirm() {
          if (dialog.hide) {
            dialog.hide();
          }
          const { allowPersonalSpace } = row;
          seletedUsers.value = [row];
          noPersonalSpace.value = !allowPersonalSpace;
          transferData?.('transferData', true);
        },
        // 直接删除
        async onCancel() {
          if (dialog.hide) {
            dialog.hide();
          }
          const { id } = row;
          const isSuccessful = await methods.deleteUser(id as number);
          if (isSuccessful) {
            directDelte?.();
          }
        },
      });
    },
    // 批量删除
    async deleteUsersFn(
      tableData: TableData,
      { transferData, directDelte }: { transferData: (a: string, b: boolean) => void; directDelte: () => void },
    ) {
      const dialog: DialogInstance = DialogPlugin.confirm({
        theme: 'warning',
        header: i18n_t('是否需要进行删除成员的数据转接？'),
        body: h => {
          const baseTip = h('p', i18n_t('可以将用户的数据和权限移交给企业其他成员'));
          return h('div', [baseTip]);
        },
        confirmBtn: i18n_t('数据转接'),
        cancelBtn: i18n_t('直接删除'),
        // 数据转接
        async onConfirm() {
          if (dialog.hide) {
            dialog.hide();
          }
          seletedUsers.value = tableData.selectedList as SMH.UserBaseInfo[];
          noPersonalSpace.value = false;
          transferData?.('transferData', true);
        },
        // 直接删除
        async onCancel() {
          if (dialog.hide) {
            dialog.hide();
          }
          const isSuccessful = await methods.deleteUsers(tableData.selectedList);
          if (isSuccessful) {
            directDelte?.();
          }
        },
      });
    },
    // 删除单个用户
    async deleteUser(userId: number) {
      let isSuccessful = true;
      try {
        await smh.userAPI.deleteUsers({
          userIds: `${userId}`,
        });
        tips.success(i18n_t('删除成功'));
      } catch (e) {
        isSuccessful = false;
        tips.error(e as MsgError);
      }
      return isSuccessful;
    },
    // 批量删除用户
    async deleteUsers(list: any[], params?: SMH.DeleteUsersTransferParams) {
      let isSuccessful = true;
      tips.loading(i18n_t('正在处理'), 0);
      try {
        await smh.userAPI.deleteUsers({
          userIds: list.map(item => item.id).join(','),
          ...params,
        });
        tips.success(params ? i18n_t('转接成功') : i18n_t('删除成功'));
      } catch (e: MsgError) {
        isSuccessful = false;
        if (e.code === 'DestUserSpaceQuotaLimited') {
          dataTransferErrorTips.value = i18n_t('该用户剩余可用存储空间不足，请重新选择');
        } else if (e.code === 'NoPersonalSpace') {
          dataTransferErrorTips.value = i18n_t('该接收用户无个人存储空间');
        } else {
          dataTransferErrorTips.value = formatError(e.code);
        }
        tips.closeAll();
      }
      return isSuccessful;
    },
    // 设置启用/冻结
    async setUserEnabled(list: any[], enabled: boolean) {
      let isSuccessful = true;
      tips.loading(i18n_t('正在处理'), 0);
      try {
        await smh.userAPI.toggleUsersAvailable({
          enabled,
          userIds: list.map(item => item.id).join(','),
        });
        const action = enabled ? i18n_t('启用') : i18n_t('冻结');
        tips.success(
          i18n_t('{action}成功', {
            action,
          }),
        );
      } catch (e: any) {
        isSuccessful = false;
        tips.error(e);
      }
      return isSuccessful;
    },
    // 切换角色
    async setUserRole(list: any[], role: string) {
      let isSuccessful = true;
      tips.loading(i18n_t('正在处理'), 0);
      try {
        await smh.userAPI.updateUsersRole({
          role,
          userIds: list.map(item => item.id).join(','),
        });
        tips.success(i18n_t('切换成功'));
      } catch (e) {
        isSuccessful = false;
        tips.error(e as MsgError);
      }
      return isSuccessful;
    },
    async getUserOwnerGroup(userIds: string) {
      let result: SMH.GetUserOwnerGroupData[] = [];
      try {
        const res = await smh.userAPI.getUserOwnerGroup({
          userIds,
        });
        result = res.data;
      } catch (e) {
        tips.error(e as MsgError);
      }
      return result;
    },
    // 激活用户
    async activeUser(userId: number) {
      let result = false;
      try {
        await smh.userAPI.activeUser({
          userId,
        });
        result = true;
        tips.success(i18n_t('激活成功'));
      } catch (e) {
        tips.error(e as MsgError);
        result = false;
      }
      return result;
    },
    // 查询空间大小
    async getUserCapacity(list: any[]) {
      try {
        const { data } = await smh.userAPI.getUserCapacity({ userIds: list.map(item => item.id).join(',') });
        const personalCapacityInfo = formatSize(data.personSpaceSizes);
        personalCapacity.value = `${personalCapacityInfo.size}${personalCapacityInfo.unit}`;
        const createdGroupCapacityInfo = formatSize(data.groupSaceSizes);
        createdGroupCapacity.value = `${createdGroupCapacityInfo.size}${createdGroupCapacityInfo.unit}`;
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
  };
  return { seletedUsers, noPersonalSpace, dataTransferErrorTips, personalCapacity, createdGroupCapacity, ...methods };
}
